import React, { useCallback, useMemo } from 'react';
import { ContactWithDetails } from './types';
import { contactsApi } from './services/contactApi';
import { SearchSection } from '../../../components/common/SearchSection';
import './ContactSearchSection.css';

interface ContactSearchSectionProps {
  // Multiple selection props
  selectedContactIds?: number[];
  onContactsChange?: (contactIds: number[]) => void;
  // Single selection props
  selectedContact?: ContactWithDetails | null;
  onContactSelect?: (contact: ContactWithDetails | null) => void;
  // Common props
  categoryTypeFilter?: string; // Optional filter for contact type (e.g., "Vendor")
  label?: string; // Custom label for the search section
  placeholder?: string; // Custom placeholder text
  maxSelections?: number; // Maximum number of contacts that can be selected (default: unlimited)
}

const ContactSearchSection: React.FC<ContactSearchSectionProps> = ({
  selectedContactIds,
  onContactsChange,
  selectedContact,
  onContactSelect,
  categoryTypeFilter,
  label = "Search Contacts",
  placeholder = "Search by first name or last name...",
  maxSelections
}) => {
  // Determine selection mode based on props
  const isSingleSelection = selectedContact !== undefined || onContactSelect !== undefined;
  const selectionMode = isSingleSelection ? 'single' : 'multiple';
  const searchFunction = useCallback(async (searchTerm: string, additionalParams?: any) => {
    const searchParams = new URLSearchParams();
    searchParams.append('search', searchTerm);
    searchParams.append('page', '1');
    searchParams.append('limit', '20');
    searchParams.append('fields', JSON.stringify(['firstName', 'lastName', 'businessName']));

    if (additionalParams?.categoryTypeFilter) {
      searchParams.append('type', additionalParams.categoryTypeFilter);
    }

    const response = await contactsApi.getAll(searchParams);
    return response.data;
  }, []);

  const renderResultItem = React.useCallback((contact: ContactWithDetails, onAdd: () => void, isDisabled?: boolean) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '12px', 
      borderBottom: '1px solid #f0f0f0',
      backgroundColor: '#ffffff'
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
          {contact.firstName} {contact.lastName}
        </div>
        {contact.businessName && (
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            {contact.businessName}
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#888' }}>
          {contact.phones?.find(p => p.isPrimary)?.phoneNumber && (
            <span style={{ marginRight: '8px' }}>
              📞 {contact.phones.find(p => p.isPrimary)?.phoneNumber}
            </span>
          )}
          {contact.emails?.find(e => e.isPrimary)?.emailAddress && (
            <span>
              ✉️ {contact.emails.find(e => e.isPrimary)?.emailAddress}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        className="app-btn app-btn-primary btn-small"
        onClick={onAdd}
        style={{ marginLeft: '8px' }}
        disabled={isDisabled}
      >
        Add
      </button>
    </div>
  ), []);

  const renderSelectedItem = React.useCallback((contact: ContactWithDetails, onRemove: () => void) => (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      padding: '8px 12px', 
      backgroundColor: '#f8f9fa', 
      border: '1px solid #e9ecef', 
      borderRadius: '4px', 
      marginBottom: '0.5rem' 
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
          {contact.firstName} {contact.lastName}
        </div>
        {contact.businessName && (
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
            {contact.businessName}
          </div>
        )}
        <div style={{ fontSize: '11px', color: '#888' }}>
          {contact.phones?.find(p => p.isPrimary)?.phoneNumber && (
            <span style={{ marginRight: '8px' }}>
              📞 {contact.phones.find(p => p.isPrimary)?.phoneNumber}
            </span>
          )}
          {contact.emails?.find(e => e.isPrimary)?.emailAddress && (
            <span>
              ✉️ {contact.emails.find(e => e.isPrimary)?.emailAddress}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        className="app-btn app-btn-danger btn-small"
        onClick={onRemove}
        title="Remove contact"
        style={{ marginLeft: '8px' }}
      >
        🗑️
      </button>
    </div>
  ), []);

  return (
    <SearchSection<ContactWithDetails>
      selectionMode={selectionMode}
      selectedIds={selectedContactIds}
      onSelectionChange={onContactsChange}
      selectedItem={selectedContact}
      onItemSelect={onContactSelect}
      searchFunction={searchFunction}
      getByIdFunction={contactsApi.getById}
      renderResultItem={renderResultItem}
      renderSelectedItem={renderSelectedItem}
      getItemId={useCallback((contact: ContactWithDetails) => contact.id, [])}
      label={label}
      placeholder={placeholder}
      maxSelections={maxSelections}
      entityType="contact"
      additionalSearchParams={useMemo(() => ({ categoryTypeFilter }), [categoryTypeFilter])}
      emptyStateMessage="No contacts associated with this property."
      selectedLabel="Associated Contacts"
    />
  );
};

export default ContactSearchSection;
